import { ReactNode, useRef, useEffect } from 'react';

import {
  EVENT_ACTION_CLOSE,
  EVENT_ACTION_OPEN,
  EVENT_TARGET_TYPE_DRAWER,
} from 'approot/shared/data-tracker/data-events.constants';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import { useVisibleDuration } from 'approot/shared/hooks/duration.hooks';
import { usePrevious } from 'approot/shared/hooks/use-previous.hooks';
import { DataTrackerEventData } from 'approot/shared/data-tracker/data-tracker.constants';
import { useDrawerLayout, useDrawerQueryStringUpdate } from './layout.hooks';
import {
  LayoutDrawerStyled,
  LayoutDrawerUnderlayStyled,
} from './layout.styles';

type DrawerSize = 'small' | 'medium' | 'large';

type Props = {
  children: ReactNode;
  isActive: boolean;
  noScrollOnContainer?: boolean;
  size?: DrawerSize;
  isSmall?: boolean; // deprecated, use size prop instead
  hasUnderlay?: boolean;
  captureKeypress?: boolean;
  queryParams?: { [key: string]: string };
  dataEvent?: {
    category: string;
    target: {
      target_identifier: string;
    } & Partial<DataTrackerEventData>;
  };
  isOnLeftSide?: boolean;
  onClose: () => void;
};

const LayoutDrawer = ({
  children,
  isActive,
  size,
  isSmall,
  noScrollOnContainer,
  hasUnderlay,
  captureKeypress,
  queryParams,
  dataEvent,
  isOnLeftSide,
  onClose,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Determine final size, with backward compatibility for isSmall prop
  const finalSize: DrawerSize = size || (isSmall ? 'small' : 'large');

  useDrawerLayout(containerRef, isActive, captureKeypress, onClose);

  const { getDurationVisible } = useVisibleDuration(isActive);

  const prevIsActive = usePrevious(isActive);

  useEffect(() => {
    if (dataEvent) {
      const {
        category,
        target: { target_identifier },
      } = dataEvent;
      if (!prevIsActive && isActive) {
        pushDataTrackerEvent(category, EVENT_ACTION_OPEN, {
          ...dataEvent.target,
          target_type: EVENT_TARGET_TYPE_DRAWER,
          target_identifier,
        });
      } else if (prevIsActive && !isActive) {
        pushDataTrackerEvent(category, EVENT_ACTION_CLOSE, {
          ...dataEvent.target,
          target_type: EVENT_TARGET_TYPE_DRAWER,
          target_identifier,
          duration: getDurationVisible(),
          follow_action: '',
        });
      }
    }
  }, [prevIsActive, isActive, dataEvent, getDurationVisible]);

  useDrawerQueryStringUpdate(isActive, queryParams);

  return (
    <>
      {hasUnderlay && (
        <LayoutDrawerUnderlayStyled $isActive={isActive} onClick={onClose} />
      )}
      <LayoutDrawerStyled
        data-selector="drawer-layout"
        $isActive={isActive}
        $size={finalSize}
        noScrollOnContainer={noScrollOnContainer}
        tabIndex={-1}
        ref={containerRef}
        $isOnLeftSide={isOnLeftSide}
      >
        {children}
      </LayoutDrawerStyled>
    </>
  );
};

export default LayoutDrawer;
